// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getRepairedDateErrorMessage,
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable,
    useAmountIndexToChange
} from '../../../shared/services/claim-shared-helper';
import AmountOfUseField from '../../../shared/components/claim-fields/amount-of-use';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimRgaStatusIndicator from '../../../shared/components/claim-rga-status-indicator';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../shared/components/claim-fields/labeled-key-value-dropdown';
import LabeledPipProductsDropDown from '../../../shared/components/claim-fields/labeled-pip-products-dropdown';
import PipClaim from '@deere/cps-it.warranty-model-factory/lib/claim-pip';
import PipDetails from '@deere/cps-it.warranty-model-factory/lib/pip-details';
import PipProductSearchResult from '@deere/cps-it.warranty-model-factory/lib/pip-product-search-results';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import {fixCursorPosition} from '../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../shared/services/format-pin';
import {isValidClaim} from '../../services/claim-validation-helper';
import {isValidDate} from '../../../../shared/services/build-date';
import moment from 'moment';

function handleInputBlur(property, value) {

    this.actions.updatePipClaimInputOnBlur(property, value, this.claim);

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts, this.claim.pin);

}

function handleUseAmountChange(amount, index) {

    const indexToChange = useAmountIndexToChange(this.product.complianceApplies, index);

    this.actions.updatePipClaimUseAmount(amount, indexToChange);

}

function handleUnitOptionChange(key) {

    this.actions.updatePipClaimUnitDropDown(key, this.product.useAmountAllowedUnits);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updatePipClaimWorkOrderOne(workOrder);

}

function handleFailureDateChange(date) {

    this.actions.updatePipClaimFailureDate(date);

}

function handleRepairDateChange(date) {

    this.actions.updatePipClaimRepairDate(date);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleOwnershipStatusChange(event) {

    this.actions.updateOwnershipStatusDropDown(event.target.value, this.ownershipStatus);

}

function handleDropDownBlur(property, event) {

    this.actions.updatePipClaimInputOnBlur(property, event.target.value);

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updatePipClaimWorkOrderTwo(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updatePipClaimRepairNumber(repairNumber);

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updatePipClaimPin(formattedPin);
    fixCursorPosition(event, formattedPin);

}

function resetCustomerProductDetails() {

    this.actions.resetCkcCustomerFailure();
    this.actions.resetCustomerDetails();
    this.actions.resetClaimAndProductDetails();

}

function handlePinBlur(pin) {

    this.actions.updatePipClaimInputOnBlur('pin', pin);

    if (!this.pipDetails.nonSerialized) {

        /* eslint-disable max-depth */

        if (pin) {

            if (pin !== this.product.pin) {

                this::resetCustomerProductDetails();
                this.actions.readClaimProduct(pin, '', true);

            }

        } else {

            this::resetCustomerProductDetails();

        }

        /* eslint-disable max-depth */

    }

}

const createPipProgram = pipProduct => ({
    description: pipProduct ? pipProduct.description : '',
    internalRecall: pipProduct ? pipProduct.internalRecall : '',
    pipNumber: pipProduct ? pipProduct.pipNumber : ''
});

function handlePipProgramDropdownChange(event) {

    const pipProduct = this.pipProducts.find(item => event.target.value === item.description);
    const pipProgram = createPipProgram(pipProduct);
    const pipId = pipProduct ? pipProduct.pipId : '';

    this.actions.updatePipProgram(pipProgram);
    this.actions.updatePipDetails(pipId);

}

function handlePipProgramDropdownBlur(event) {

    const pipProduct = this.pipProducts.find(item => event.target.value === item.description);
    const pipNumber = pipProduct ? pipProduct.pipNumber : '';

    this.actions.validatePipNumber(pipNumber);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ?
        moment.utc(date.target.value).valueOf() :
        null;

    this.actions.updatePipClaimInputOnBlur(property, modifiedDate);

}

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
                {props.claim.rgaStatus.status && props.claim.rgaStatus.status !== 'NONE' &&
                    <ClaimRgaStatusIndicator
                        claim={props.claim.claimNumber}
                        claimRgaStatus={props.claim.rgaStatus}
                    />
                }
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='pin'
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
                {props.isNonTracked ?
                    <LabeledInputWithBlur
                        className='pip-program'
                        errorMessage='PIP_PROGRAM_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pipProgram', props.isEditable)}
                        isEditable={false}
                        isValid={true}
                        label='PIP_PROGRAM'
                        value={props.claim.pipProgram.pipNumber}
                        vsrMessage={getVsrMessage(props.claim.errors, 'pipProgram', props.isEditable)}
                    /> :
                        <LabeledPipProductsDropDown
                            className='pip-program'
                            errorMessage='PIP_PROGRAM_IS_REQUIRED'
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pipProgram', props.isEditable)}
                            isEditable={props.isEditable}
                            isValid={props.formValidation.pipNumber}
                            label='PIP_PROGRAM'
                            list={props.pipProducts}
                            onBlur={props::handlePipProgramDropdownBlur}
                            onChange={props::handlePipProgramDropdownChange}
                            selectedValue={props.claim.pipProgram.description}
                            vsrMessage={getVsrMessage(props.claim.errors, 'pipProgram', props.isEditable)}
                        />
                }
            </div>
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className='ownership-status'
                    errorMessage='OWNERSHIP_STATUS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'ownershipStatus', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.ownershipStatus}
                    label='OWNERSHIP_STATUS'
                    list={props.ownershipStatus}
                    onBlur={handleDropDownBlur.bind(props, 'ownershipStatus')}
                    onChange={props::handleOwnershipStatusChange}
                    selectedValue={props.claim.referenceInfo.ownershipStatus}
                    vsrMessage={getVsrMessage(props.claim.errors, 'ownershipStatus', props.isEditable)}
                />
            </div>
            <div className='failure-info'>
                <LabeledDateInput
                    className='failure-date'
                    date={props.claim.failureInfo.failureDate}
                    errorMessage='FAILED_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failureDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failureDate}
                    label='FAILED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'failureDate')}
                    onDateChange={props::handleFailureDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failureDate', props.isEditable)}
                />
                <LabeledDateInput
                    className='repair-date'
                    date={props.claim.failureInfo.repairDate}
                    errorMessage={getRepairedDateErrorMessage(props.claim.failureInfo.repairDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.repairDate && isRepairDateAfterFailureDate(props.claim.failureInfo.repairDate, props.claim.failureInfo.failureDate)}
                    label='REPAIRED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'repairDate')}
                    onDateChange={props::handleRepairDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                />
                {!props.pipDetails.nonSerialized &&
                    <AmountOfUseField
                        allowedUnits={props.product.useAmountAllowedUnits}
                        complianceApplies={props.product.complianceApplies}
                        errors={props.claim.errors}
                        formValidation={props.formValidation}
                        isEditable={props.isEditable}
                        onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                        onInputChange={props::handleUseAmountChange}
                        onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                        onOptionChange={props::handleUnitOptionChange}
                        useAmounts={props.claim.useAmounts}
                    />
                }
                <LabeledInputWithBlur
                    className='failure-part'
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failurePart}
                    label='FAILED_PART'
                    maxLength={18}
                    value={props.claim.failureInfo.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />
            </div>
            {(props.isEditable && props.claim.showValidate) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim, props.pipDetails.nonSerialized)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(PipClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isNonTracked: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    ownershipStatus: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    pipDetails: PropTypes.instanceOf(PipDetails).isRequired,
    pipProducts: PropTypes.arrayOf(PropTypes.instanceOf(PipProductSearchResult)).isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/pip/components/claim-details-section/claim-details-container.js